import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { MarketPage } from '@/components/MarketPage';
import { ProfilePage } from '@/components/ProfilePage';
import { LoginPage } from '@/pages/LoginPage';
import { ProductPage } from '@/pages/ProductPage';
import { ListingPage } from '@/pages/ListingPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { TermsPage } from '@/pages/TermsPage';
import { FaqPage } from '@/pages/FaqPage';
import { B2BApiPage } from '@/pages/B2BApiPage';
import { AdminPage } from '@/pages/AdminPage';
import { MaintenancePage } from '@/pages/MaintenancePage';
import { PartnerLoginPage } from '@/pages/PartnerLoginPage';
import { PartnerPage } from '@/pages/PartnerPage';
import { PaymentCallbackPage } from '@/pages/PaymentCallbackPage';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { PartnerProvider } from '@/contexts/PartnerContext';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { getMaintenance, onMaintenanceChange, setMaintenance } from '@/lib/maintenanceState';
import { getPublicMaintenanceStatus } from '@/lib/api';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { profileApi } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import logoUrl from '@/assets/logo.png';


function AppContent() {
  const [currentPage, setCurrentPage] = useState<'market' | 'profile'>('market');
  const [showSupport, setShowSupport] = useState(false);
  const [maintenance, setMaintenanceState] = useState(getMaintenance());
  const { user, isLoading, isAuthenticated, login, logout, refreshUser } = useAuth();

  useEffect(() => onMaintenanceChange((enabled, message) =>
    setMaintenanceState({ enabled, message })
  ), []);

  // Maintenance mode: показываем заглушку всем кроме админов.
  // Админ должен видеть нормальный UI, чтобы dis-toggle'нуть режим.
  // MaintenancePage сама показывает кнопку «Войти через Steam» если
  // юзер не авторизован — авторизация работает в maintenance (login
  // endpoints в allowlist), а после авторизации middleware bypass'ит
  // админа.
  if (maintenance.enabled && !user?.is_admin) {
    return (
      <MaintenancePage
        message={maintenance.message}
        isAuthenticated={isAuthenticated}
        onLogin={login}
      />
    );
  }

  const handleUpdateTradeUrl = async (tradeUrl: string) => {
    try {
      await profileApi.updateProfile({ steam_trade_url: tradeUrl });
      toast.success('Trade URL обновлён');
      await refreshUser();
    } catch (error: any) {
      toast.error(error.message || 'Ошибка обновления Trade URL');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff6b00]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1117] flex flex-col">
      <Header
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        isLoggedIn={isAuthenticated}
        onLogin={login}
        onLogout={logout}
        user={user}
      />

      <main className="flex-1 w-full">
        <Routes>
          <Route path="/login" element={
            // AuthContext автоматически обработает токены из URL если они есть
            isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
          } />
          <Route path="/" element={
            <MarketPage />
          } />
          <Route path="/market/:slug" element={<ProductPage />} />
          <Route path="/listing/:id" element={<ListingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/profile" element={
            isAuthenticated ? (
              <ProfilePage
                user={user}
                isAuthenticated={isAuthenticated}
                onLogin={login}
                onUpdateTradeUrl={handleUpdateTradeUrl}
                onRefreshUser={refreshUser}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="/b2b-api" element={<B2BApiPage />} />
          <Route path="/admin" element={
            user?.is_admin ? <AdminPage /> : <Navigate to="/" replace />
          } />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#1a2332] bg-[#0d1117]">
        {/* Main footer content */}
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-[1920px] mx-auto py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo and description */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src={logoUrl}
                  alt="Xpanda"
                  className="w-9 h-9 object-contain flex-shrink-0"
                />
                <span className="text-white font-bold">MARKET</span>
              </div>
              <p className="text-[#8b949e] text-sm">
                Маркетплейс скинов CS2. Безопасные сделки, мгновенная доставка, честные цены.
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h3 className="text-white font-semibold mb-4">Навигация</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/" className="text-[#8b949e] hover:text-[#ff6b00] transition-colors">
                    Маркет
                  </Link>
                </li>
                <li>
                  <Link to={isAuthenticated ? '/profile' : '/login'} className="text-[#8b949e] hover:text-[#ff6b00] transition-colors">
                    Профиль
                  </Link>
                </li>
                <li>
                  <Link to="/b2b-api" className="text-[#8b949e] hover:text-[#ff6b00] transition-colors">
                    B2B API
                  </Link>
                </li>
                <li>
                  <button
                    onClick={() => setShowSupport(true)}
                    className="text-[#8b949e] hover:text-[#ff6b00] transition-colors"
                  >
                    Поддержка
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-white font-semibold mb-4">Правовая информация</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy" className="text-[#8b949e] hover:text-[#ff6b00] transition-colors">
                    Политика конфиденциальности
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-[#8b949e] hover:text-[#ff6b00] transition-colors">
                    Публичная оферта
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="text-[#8b949e] hover:text-[#ff6b00] transition-colors">
                    FAQ
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal entity */}
            <div>
              <h3 className="text-white font-semibold mb-4">Юридическое лицо</h3>
              <div className="text-[#8b949e] text-sm space-y-1">
                <p className="text-[#c9d1d9] font-medium">MEGA PANDA, LLC</p>
                <p>NCR: 0000999047</p>
                <p className="text-xs leading-relaxed">
                  UL. BOHATERÓW MONTE CASSINO 39-14<br />
                  PL-81-767, SOPOT, POLAND
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#1a2332]">
          <div className="w-full px-4 sm:px-6 lg:px-8 max-w-[1920px] mx-auto py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-[#6e7681] text-sm">
                © 2026 Xpanda Market. Все права защищены.
              </p>
              <p className="text-[#6e7681] text-sm">
                Не связано с Valve Corporation. CS2 и Steam являются товарными знаками Valve Corporation.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Support modal */}
      <Dialog open={showSupport} onOpenChange={setShowSupport}>
        <DialogContent className="bg-[#161b22] border-[#30363d] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">Поддержка</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex items-start gap-3 p-3 bg-[#0d1117] rounded-lg border border-[#30363d]">
              <span className="text-2xl">✉️</span>
              <div>
                <p className="text-[#8b949e] text-xs mb-1">Email</p>
                <a
                  href="mailto:site@xpanda.team"
                  className="text-[#ff6b00] hover:underline font-medium"
                >
                  site@xpanda.team
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-[#0d1117] rounded-lg border border-[#30363d]">
              <span className="text-2xl">💬</span>
              <div>
                <p className="text-[#8b949e] text-xs mb-1">Telegram</p>
                <a
                  href="https://t.me/xpanda_support"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#ff6b00] hover:underline font-medium"
                >
                  @xpanda_support
                </a>
              </div>
            </div>
            <p className="text-[#6e7681] text-xs text-center">
              Среднее время ответа — до 24 часов
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#161b22',
            border: '1px solid #30363d',
            color: '#fff',
          },
        }}
      />
    </div>
  );
}

function App() {
  // Pre-fetch maintenance flag ДО рендера маркета — иначе SPA успевает
  // нарисовать market'у которая через ~1s сменяется MaintenancePage'ом
  // (мигание). Endpoint /api/v1/maintenance/status — публичный
  // read-only, доступен даже в режиме обслуживания.
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const { enabled, message } = await getPublicMaintenanceStatus();
        setMaintenance(enabled, message);
      } finally {
        setBootstrapped(true);
      }
    })();
  }, []);

  if (!bootstrapped) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff6b00]" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Callback после оплаты Invizo — без авторизации, без maintenance guard */}
        <Route path="/payment-callback" element={<PaymentCallbackPage />} />

        {/* Partner cabinet — own layout, own auth */}
        <Route path="/partner/*" element={
          <PartnerProvider>
            <Toaster position="bottom-right" toastOptions={{ style: { background: '#161b22', border: '1px solid #30363d', color: '#fff' } }} />
            <Routes>
              <Route path="login" element={<PartnerLoginPage />} />
              <Route path="*" element={<PartnerPage />} />
            </Routes>
          </PartnerProvider>
        } />

        {/* Main marketplace */}
        <Route path="/*" element={
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
